import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { CircuitTemplate, CircuitSchema } from '../types';
import { apiService } from '../services/apiService';
import { useEscapeKey } from '../hooks/useEscapeKey';

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  width: 560px;
  max-height: 520px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e8eaed;
`;

const HeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #5f6368;
  border-radius: 4px;

  &:hover {
    background: #f1f3f4;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Card = styled.div`
  border: 1px solid #e8eaed;
  border-radius: 6px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #dadce0;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  }
`;

const CardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  line-height: 1.3;
`;

const CardDescription = styled.div`
  font-size: 12px;
  color: #5f6368;
  line-height: 1.4;
  flex: 1;
`;

const UseButton = styled.button`
  align-self: flex-start;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  background: #1a73e8;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1765cc;
  }

  &:active {
    background: #1558b0;
  }

  &:disabled {
    background: #dadce0;
    cursor: default;
  }
`;

const StateMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #9aa0a6;
  font-size: 13px;
`;

const ErrorMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #c5221f;
  font-size: 13px;
`;

interface TemplatePickerModalProps {
  onSelect: (schema: CircuitSchema) => void;
  onClose: () => void;
}

export const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({
  onSelect,
  onClose,
}) => {
  const [templates, setTemplates] = useState<CircuitTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<number | null>(null);

  useEscapeKey(onClose);

  /**
   * The list endpoint uses a lighter serializer that omits `schema`, so the
   * full template has to be fetched before it can be applied.
   */
  const handleUse = useCallback(async (template: CircuitTemplate) => {
    if (template.schema) {
      onSelect(template.schema);
      return;
    }

    setApplyingId(template.id);
    setError(null);
    try {
      const full = await apiService.getTemplate(template.id);
      if (full.schema) {
        onSelect(full.schema);
      } else {
        setError(`Template "${template.name}" has no circuit data.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setApplyingId(null);
    }
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;

    const loadTemplates = async () => {
      try {
        const data = await apiService.getTemplates();
        if (!cancelled) {
          setTemplates(data.filter((t) => t.is_active));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load templates');
          setLoading(false);
        }
      }
    };

    loadTemplates();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderTitle>Circuit Templates</HeaderTitle>
          <CloseButton onClick={onClose}>
            <Icon path={mdiClose} size={0.7} />
          </CloseButton>
        </Header>

        <Content>
          {loading ? (
            <StateMessage>Loading templates...</StateMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : templates.length === 0 ? (
            <StateMessage>No templates available.</StateMessage>
          ) : (
            <Grid>
              {templates.map((template) => (
                <Card key={template.id}>
                  <CardName>{template.name}</CardName>
                  <CardDescription>{template.description}</CardDescription>
                  <UseButton
                    onClick={() => handleUse(template)}
                    disabled={applyingId !== null}
                  >
                    {applyingId === template.id ? 'Loading...' : 'Use Template'}
                  </UseButton>
                </Card>
              ))}
            </Grid>
          )}
        </Content>
      </Panel>
    </Overlay>
  );
};
